import _styled, { default as _styled2, default as _styled3 } from "styled-components";
var _templateObject, _templateObject2;
function _taggedTemplateLiteral(e, t) { return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, { raw: { value: Object.freeze(t) } })); }
import { runInAction } from "mobx";
import { observer, useLocalObservable } from "mobx-react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import isDefined from "../../../Core/isDefined";
import Icon from "../../../Styled/Icon";
import { CenturyView } from "./DatePicker/CenturyView";
import { DayView } from "./DatePicker/DayView";
import { HourView } from "./DatePicker/HourView";
import { MonthView } from "./DatePicker/MonthView";
import { TimeListView } from "./DatePicker/TimeListView";
import { YearView } from "./DatePicker/YearView";
import { BackButton } from "./DatePicker/DateTimePickerStyles";
import { OutsideClickHandler } from "../../Generic/OutsideClickHandler";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var GranularityEnum = /*#__PURE__*/function (GranularityEnum) {
  GranularityEnum[GranularityEnum["century"] = 0] = "century";
  GranularityEnum[GranularityEnum["year"] = 1] = "year";
  GranularityEnum[GranularityEnum["month"] = 2] = "month";
  GranularityEnum[GranularityEnum["day"] = 3] = "day";
  GranularityEnum[GranularityEnum["hour"] = 4] = "hour";
  GranularityEnum[GranularityEnum["minutes"] = 5] = "minutes";
  return GranularityEnum;
}(GranularityEnum || {});
const DateTimePicker = _ref => {
  let {
    dates,
    currentDate,
    onChange,
    openDirection = "down",
    isOpen,
    onClose,
    dateFormat
  } = _ref;
  const {
    t
  } = useTranslation();
  const store = useLocalObservable(() => {
    let defaultCentury;
    let defaultYear;
    let defaultMonth;
    let defaultDay;
    let defaultGranularity = GranularityEnum.century;
    if (dates) {
      if (dates.index.length === 1) {
        // only one century
        const soleCentury = dates.index[0];
        const dataFromThisCentury = dates[soleCentury];
        defaultCentury = soleCentury;
        defaultGranularity = GranularityEnum.year;
        if (dataFromThisCentury.index.length === 1) {
          // only one year, check if this year has only one month
          const soleYear = dataFromThisCentury.index[0];
          const dataFromThisYear = dataFromThisCentury[soleYear];
          defaultYear = soleYear;
          defaultGranularity = GranularityEnum.month;
          if (dataFromThisYear.index.length === 1) {
            // only one month data from this one year, need to check day then
            const soleMonth = dataFromThisYear.index[0];
            const dataFromThisMonth = dataFromThisYear[soleMonth];
            defaultMonth = soleMonth;
            defaultGranularity = GranularityEnum.day;
            if (dataFromThisMonth.index.length === 1) {
              // only one day has data
              defaultDay = dataFromThisMonth.index[0];
              defaultGranularity = GranularityEnum.hour;
            }
          }
        }
      }
    }
    return {
      century: defaultCentury,
      year: defaultYear,
      month: defaultMonth,
      day: defaultDay,
      hour: undefined,
      time: undefined,
      get currentView() {
        const {
          century,
          year,
          month,
          day,
          hour
        } = this;
        if (!isDefined(century)) {
          return dates.dates.length >= 12 ? {
            view: "century",
            dates
          } : {
            view: "time",
            dates: dates.dates
          };
        }
        const centuryData = dates[century];
        if (!isDefined(year)) {
          return centuryData.dates.length > 12 ? {
            view: "year",
            dates: centuryData
          } : {
            view: "time",
            dates: centuryData.dates
          };
        }
        const yearData = centuryData[year];
        if (!isDefined(month)) {
          return yearData.dates.length > 12 ? {
            view: "month",
            dates: yearData
          } : {
            view: "time",
            dates: yearData.dates
          };
        }
        const monthData = yearData[month];
        if (!isDefined(day)) {
          return monthData.dates.length > 31 ? {
            view: "day",
            dates: monthData
          } : {
            view: "time",
            dates: monthData.dates
          };
        }
        const dayData = monthData[day];
        if (!isDefined(hour)) {
          return dayData.dates.length > 24 ? {
            view: "hour",
            dates: dayData
          } : {
            view: "time",
            dates: dayData.dates
          };
        }
        return {
          view: "time",
          dates: dayData[hour]
        };
      },
      get granularity() {
        if (!isDefined(this.century)) return GranularityEnum.century;
        if (!isDefined(this.year)) return GranularityEnum.year;
        if (!isDefined(this.month)) return GranularityEnum.month;
        if (!isDefined(this.day)) return GranularityEnum.day;
        if (!isDefined(this.hour)) return GranularityEnum.hour;
        return GranularityEnum.minutes;
      },
      get canGoBack() {
        return this.granularity > defaultGranularity;
      },
      goBack() {
        if (this.selectedDate) {
          if (!isDefined(this.month)) {
            this.year = undefined;
            this.day = undefined;
          }
          if (!isDefined(this.hour)) {
            this.day = undefined;
          }
          if (!isDefined(this.day)) {
            this.month = undefined;
          }
          this.hour = undefined;
          this.selectedDate = undefined;
        } else if (isDefined(this.hour)) this.hour = undefined;else if (isDefined(this.day)) this.day = undefined;else if (isDefined(this.month)) this.month = undefined;else if (isDefined(this.year)) this.year = undefined;else if (isDefined(this.century)) this.century = undefined;
        this.selectedDate = undefined;
      },
      clearSelection() {
        this.century = undefined;
        this.year = undefined;
        this.month = undefined;
        this.day = undefined;
        this.hour = undefined;
        this.selectedDate = undefined;
      },
      setDate(date) {
        if (!date) {
          this.clearSelection();
          return;
        }
        this.selectedDate = date;
      },
      selectCentury(century) {
        this.century = century;
        this.year = undefined;
        this.month = undefined;
        this.day = undefined;
        this.hour = undefined;
        this.selectedDate = undefined;
      },
      selectYear(year) {
        this.year = year;
        this.month = undefined;
        this.day = undefined;
        this.hour = undefined;
        this.selectedDate = undefined;
      },
      selectMonth(month) {
        this.month = month;
        this.day = undefined;
        this.hour = undefined;
        this.selectedDate = undefined;
      },
      selectDay(day) {
        this.day = day;
        this.hour = undefined;
        this.selectedDate = undefined;
      },
      selectHour(hour) {
        this.hour = hour;
        this.selectedDate = undefined;
      }
    };
  });
  useEffect(() => {
    if (isDefined(currentDate)) {
      runInAction(() => {
        store.day = isDefined(store.day) ? currentDate.getDate() : undefined;
        store.month = isDefined(store.month) ? currentDate.getMonth() : undefined;
        store.year = isDefined(store.year) ? currentDate.getFullYear() : undefined;
        store.century = isDefined(store.century) ? Math.floor(currentDate.getFullYear() / 100) : undefined;
        store.selectedDate = currentDate;
      });
    }
  }, [currentDate, store]);
  if (!dates || !isOpen) return null;
  return /*#__PURE__*/_jsx(OutsideClickHandler, {
    disabled: !isOpen,
    onOutsideClick: onClose,
    children: /*#__PURE__*/_jsx(_StyledDiv, {
      onClick: event => {
        event.stopPropagation();
      },
      children: /*#__PURE__*/_jsxs(_StyledDiv2, {
        className: "scrollbars",
        $_css: openDirection === "down" ? "\n          top: 40px;\n          left: -190px;\n        " : "",
        children: [/*#__PURE__*/_jsx(_StyledDiv3, {
          children: /*#__PURE__*/_jsx(BackButton, {
            title: t("dateTime.back"),
            disabled: !store.canGoBack,
            type: "button",
            onClick: store.goBack,
            children: /*#__PURE__*/_jsx(Icon, {
              glyph: Icon.GLYPHS.left
            })
          })
        }), (store.currentView.view === "time" || store.currentView.view === "minutes") && /*#__PURE__*/_jsx(TimeListView, {
          items: store.currentView.dates,
          dateFormatString: dateFormat,
          onTimeSelected: time => {
            store.setDate(time);
            onChange(time);
            onClose();
          }
        }), store.currentView.view === "century" && /*#__PURE__*/_jsx(CenturyView, {
          datesObject: store.currentView.dates,
          onSelectCentury: store.selectCentury
        }), store.currentView.view === "year" && /*#__PURE__*/_jsx(YearView, {
          datesObject: store.currentView.dates,
          onSelectYear: store.selectYear
        }), store.currentView.view === "month" && /*#__PURE__*/_jsx(MonthView, {
          year: store.year,
          datesObject: store.currentView.dates,
          onSelectMonth: store.selectMonth,
          onBack: store.goBack
        }), store.currentView.view === "day" && /*#__PURE__*/_jsx(DayView, {
          year: store.year,
          month: store.month,
          datesObject: store.currentView.dates,
          selectedDay: store.day,
          onSelectDay: date => store.selectDay(date === null || date === void 0 ? void 0 : date.getDate()),
          onBackToYear: () => {
            store.selectYear(store.year);
          },
          onBackToMonth: () => {
            store.selectMonth(store.month);
          }
        }), store.currentView.view === "hour" && /*#__PURE__*/_jsx(HourView, {
          year: store.year,
          month: store.month,
          day: store.day,
          datesObject: store.currentView.dates,
          onSelectHour: store.selectHour
        })]
      })
    })
  });
};
export default observer(DateTimePicker);
var _StyledDiv = _styled("div")(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n          color: ", ";\n          display: table-cell;\n          width: 30px;\n          height: 30px;\n        "])), p => p.theme.textLight);
var _StyledDiv2 = _styled2("div")(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n            background: ", ";\n            width: 260px;\n            height: 300px;\n            border: 1px solid ", ";\n            border-radius: 5px;\n            padding: 5px;\n            position: relative;\n            top: -170px;\n            left: 0;\n            z-index: 100;\n\n            ", "\n          "])), p => p.theme.dark, p => p.theme.grey, p => p.$_css);
var _StyledDiv3 = _styled3("div").withConfig({
  displayName: "DateTimePicker___StyledDiv3",
  componentId: "sc-16tonqk-0"
})(["padding-bottom:5px;padding-right:5px;"]);